<template>
  <div>
    <div class="smeta-sticky-top">
      <!-- Вкладки верхнего уровня -->
      <div class="hub-tabs-row">
        <div class="hub-tabs">
          <button
            class="hub-tab"
            :class="{ 'hub-tab--active': activeTab === 'smeta_new' }"
            @click="setTab('smeta_new')"
          >
            Смета новая
          </button>
          <button
            class="hub-tab"
            :class="{ 'hub-tab--active': activeTab === 'compare' }"
            @click="setTab('compare')"
          >
            Сравнение сметы
          </button>
          <button
            class="hub-tab"
            :class="{ 'hub-tab--active': activeTab === 'manager' }"
            @click="setTab('manager')"
          >
            Смета менеджера
          </button>
          <button
            class="hub-tab"
            :class="{ 'hub-tab--active': activeTab === 'anomalies' }"
            @click="setTab('anomalies')"
          >
            Журнал изменений
          </button>
          <button
            class="hub-tab"
            :class="{ 'hub-tab--active': activeTab === 'smeta_restore' }"
            @click="setTab('smeta_restore')"
          >
            Восстановление сметы
          </button>
        </div>
      </div>

      <!-- Фильтры «Смета новая»: Подъезд / Этаж / Работы — между вкладками и под-вкладками -->
      <div v-if="activeTab === 'smeta_new'" class="smeta-new-filters-row">
        <CustomSelect
          :options="entranceOptions"
          label="Подъезд"
          option-label-key="label"
          option-value-key="project_entrance_id"
          :value="smetaNewEntranceId"
          @change="onEntranceChange"
          placeholder="Все подъезды"
          :clearable="false"
        />
        <CustomSelect
          :options="floorOptions"
          label="Этаж"
          option-label-key="label"
          option-value-key="floor_map_id"
          :value="smetaNewFloorMapId"
          @change="onFloorChange"
          placeholder="Все этажи"
          :clearable="false"
          :disabled="!smetaNewEntranceId"
        />
        <CustomSelect
          :multiple="true"
          :options="workSetSelectOptions"
          label="Работы"
          option-label-key="label"
          option-value-key="work_set_id"
          :value="smetaNewWorkSetIds"
          :loading="workSetsLoading"
          placeholder="Все работы"
          :clearable="true"
          @change="onWorkSetsFilterChange"
        />
      </div>

      <!-- Sub-tabs общая / подрядчик / команда -->
      <div v-if="activeTab === 'smeta_new'" class="smeta-tabs">
        <button
          class="smeta-tab"
          :class="{ 'smeta-tab--active': smetaNewWhat === 'general' }"
          @click="onWhatChange('general')"
        >
          Общая смета
        </button>
        <button
          class="smeta-tab"
          :class="{ 'smeta-tab--active': smetaNewWhat === 'contractor' }"
          @click="onWhatChange('contractor')"
        >
          Смета подрядчика
        </button>
        <button
          class="smeta-tab"
          :class="{ 'smeta-tab--active': smetaNewWhat === 'team' }"
          @click="onWhatChange('team')"
        >
          Смета команды проекта
        </button>
      </div>

      <!-- Фильтры «Восстановление сметы»: Подъезд / Этаж -->
      <div v-if="activeTab === 'smeta_restore'" class="smeta-new-filters-row">
        <CustomSelect
          :options="entranceOptions"
          label="Подъезд"
          option-label-key="label"
          option-value-key="project_entrance_id"
          :value="smetaNewEntranceId"
          @change="onEntranceChange"
          placeholder="Все подъезды"
          :clearable="false"
        />
        <CustomSelect
          :options="floorOptions"
          label="Этаж"
          option-label-key="label"
          option-value-key="floor_map_id"
          :value="smetaNewFloorMapId"
          @change="onFloorChange"
          placeholder="Все этажи"
          :clearable="false"
          :disabled="!smetaNewEntranceId"
        />
      </div>

      <div v-if="activeTab === 'smeta_restore'" class="smeta-restore-sticky-row">
        <!-- Якорь для Teleport из ProjectSmetaRestore: версия, «Загрузить», «Перенести выбранные» -->
        <div ref="restoreStickyActionsHost" class="smeta-restore-sticky-actions" />
      </div>
    </div>

    <!-- ── Смета новая ─────────────────────────────────────── -->
    <div v-if="activeTab === 'smeta_new'" class="hub-panel">
      <ProjectSmetaNew
        :project-id="projectId"
        :resident-id="residentId"
        :project-entrance-id="smetaNewEntranceId"
        :floor-map-id="smetaNewFloorMapId"
        :what="smetaNewWhat"
        :work-set-ids="smetaNewWorkSetIds"
      />
    </div>

    <!-- ── Сравнение сметы ────────────────────────────────── -->
    <div v-else-if="activeTab === 'compare'" class="hub-panel">
      <ProjectSmetaCompareNew :project-id="projectId" />
    </div>

    <!-- ── Смета менеджера ────────────────────────────────── -->
    <div v-else-if="activeTab === 'manager'" class="hub-panel">
      <ProjectSmetaManager
        :project-id="projectId"
        :resident-id="residentId"
        :project-type-id="projectTypeId"
      />
    </div>

    <!-- ── Журнал изменений ───────────────────────────────── -->
    <div v-else-if="activeTab === 'anomalies'" class="hub-panel">
      <ProjectSmetaAnomalies :project-id="projectId" />
    </div>

    <!-- ── Восстановление сметы ───────────────────────────── -->
    <div v-else-if="activeTab === 'smeta_restore'" class="hub-panel">
      <ProjectSmetaRestore
        :project-id="projectId"
        :project-entrance-id="smetaNewEntranceId"
        :floor-map-id="smetaNewFloorMapId"
        :what="'general'"
      />
    </div>

  </div>
</template>

<script>
import { getProjectEntrances, getProjectEntranceFloors, getSmetaNewWorkSetsInScope } from './services';
import ProjectSmetaNew from './ProjectSmetaNew.vue';
import ProjectSmetaCompareNew from './ProjectSmetaCompareNew.vue';
import ProjectSmetaManager from './ProjectSmetaManager.vue';
import ProjectSmetaAnomalies from './ProjectSmetaAnomalies.vue';
import ProjectSmetaRestore from './ProjectSmetaRestore.vue';
import CustomSelect from '@/components/form/CustomSelect';

export default {
  name: 'ProjectSmetaHub',
  components: { ProjectSmetaNew, ProjectSmetaCompareNew, ProjectSmetaManager, ProjectSmetaAnomalies, ProjectSmetaRestore, CustomSelect },
  provide() {
    return {
      restoreStickyActionsHost: () => this.$refs.restoreStickyActionsHost,
    };
  },
  props: {
    projectId:     { type: Number, required: true },
    residentId:    { type: Number, default: null },
    projectTypeId: { type: Number, default: null },
    initialTab:    { type: String, default: 'smeta_new' },
  },
  data() {
    return {
      activeTab: this.initialTab || 'smeta_new',
      // Смета новая — фильтры
      smetaNewWhat: 'general',
      smetaNewEntranceId: null,
      smetaNewFloorMapId: null,
      smetaNewEntrances: [],
      smetaNewFloors: [],
      smetaNewWorkSetOptions: [],
      smetaNewWorkSetIds: [],
      workSetsLoading: false,
    };
  },
  computed: {
    entranceOptions() {
      const base = Array.isArray(this.smetaNewEntrances) ? this.smetaNewEntrances : [];
      return [
        { project_entrance_id: null, label: 'Все подъезды' },
        ...base,
      ];
    },
    floorOptions() {
      const base = Array.isArray(this.smetaNewFloors) ? this.smetaNewFloors : [];
      return [
        { floor_map_id: null, label: 'Все этажи' },
        ...base.map((f) => ({ floor_map_id: f.floor_map_id, label: `Этаж ${f.level_num}` })),
      ];
    },
    workSetSelectOptions() {
      const base = Array.isArray(this.smetaNewWorkSetOptions) ? this.smetaNewWorkSetOptions : [];
      return base.map((r) => ({
        work_set_id: r.work_set_id,
        label: r.work_set_name || String(r.work_set_id),
      }));
    },
  },
  watch: {
    projectId() {
      this.loadEntrances().then(() => this.loadWorkSetOptions());
    },
    activeTab(tab) {
      if (tab === 'smeta_new') this.loadWorkSetOptions();
    },
  },
  methods: {
    setTab(tab) {
      this.activeTab = tab;
      // Обновляем URL query для возможности передать ссылку на конкретную вкладку
      if (this.$router) {
        this.$router.replace({ query: { ...this.$route?.query, tab } }).catch(() => {});
      }
    },
    async loadEntrances() {
      if (!this.projectId) { this.smetaNewEntrances = []; return; }
      const res = await getProjectEntrances(this.projectId);
      this.smetaNewEntrances = res || [];
    },
    async loadFloors() {
      if (!this.projectId || !this.smetaNewEntranceId) {
        this.smetaNewFloors = [];
        this.smetaNewFloorMapId = null;
        return;
      }
      const entrance = this.smetaNewEntrances.find(
        (e) => e.project_entrance_id === this.smetaNewEntranceId
      );
      if (!entrance) { this.smetaNewFloors = []; this.smetaNewFloorMapId = null; return; }
      const res = await getProjectEntranceFloors(this.projectId, entrance.entrance);
      this.smetaNewFloors = res || [];
    },
    async loadWorkSetOptions() {
      if (!this.projectId) {
        this.smetaNewWorkSetOptions = [];
        this.smetaNewWorkSetIds = [];
        return;
      }
      this.workSetsLoading = true;
      try {
        const rows = await getSmetaNewWorkSetsInScope(
          this.projectId,
          this.smetaNewEntranceId || null,
          this.smetaNewFloorMapId || null,
        ) || [];
        this.smetaNewWorkSetOptions = rows;
        const allowed = new Set(rows.map((r) => r.work_set_id));
        this.smetaNewWorkSetIds = (this.smetaNewWorkSetIds || []).filter((id) => allowed.has(id));
      } finally {
        this.workSetsLoading = false;
      }
    },
    async onEntranceChange(value) {
      this.smetaNewEntranceId = value;
      this.smetaNewFloorMapId = null;
      this.smetaNewFloors = [];
      await this.loadFloors();
      await this.loadWorkSetOptions();
    },
    async onFloorChange(value) {
      this.smetaNewFloorMapId = value;
      await this.loadWorkSetOptions();
    },
    onWorkSetsFilterChange(val) {
      this.smetaNewWorkSetIds = Array.isArray(val) ? [...val] : [];
    },
    onWhatChange(value) {
      if (this.smetaNewWhat === value) return;
      this.smetaNewWhat = value;
    },
  },
  async mounted() {
    await this.loadEntrances();
    await this.loadWorkSetOptions();
  },
};
</script>

<style scoped lang="scss">
.smeta-sticky-top {
  position: sticky;
  top: 66px;
  z-index: 10;
  background: #fff;
  padding-bottom: 4px;
}

.hub-tabs-row {
  border-bottom: 2px solid #e5e7eb;
}

/* Главные вкладки хаба */
.hub-tabs {
  display: flex;
  gap: 0;
}

/* Строка фильтров между вкладками и под-вкладками */
.smeta-new-filters-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0 4px;
  border-bottom: 1px solid #e5e7eb;

  > * {
    min-width: 160px;
    flex: 1 1 160px;
    max-width: 280px;

    &:last-child {
      flex: 2 1 240px;
      max-width: 480px;
    }
  }
}

.hub-tab {
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #374151;
    border-bottom-color: #d1d5db;
  }
}

.hub-tab--active {
  color: #2f80ed;
  border-bottom-color: #2f80ed;
  font-weight: 600;
}

/* Sub-tabs для Сметы новой */
.smeta-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  gap: 0;
  margin-top: 8px;
}

.smeta-tab {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #374151;
    border-bottom-color: #d1d5db;
  }
}

.smeta-tab--active {
  color: #2f80ed;
  border-bottom-color: #2f80ed;
  font-weight: 600;
}

/* Восстановление сметы: подвкладки + действия в одной липкой строке */
.smeta-restore-sticky-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px 20px;
  border-bottom: 2px solid #e5e7eb;
  margin-top: 8px;
}

.smeta-restore-sticky-actions {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  padding-bottom: 6px;
  margin-left: auto;
}

.hub-panel {
  min-height: 200px;
}

</style>
